import { mergeQuestionSummaries, summarizeAnswers, type AnswerGroupContext } from './answerGroupSummaries';
import type { AnswerGroup, MessageRef, NewReportData } from './types';
import { calculateCustomMetrics } from '@arnold/core';

type Response = NewReportData['responses'][0];

function hasAnyMessage(response: Response): boolean {
  return (response.messages?.length ?? 0) > 0;
}

function collectAnswers(responses: Response[]): Map<string, MessageRef[]> {
  const answers = new Map<string, MessageRef[]>();
  const seen = new Set<string>();
  for (const response of responses) {
    for (const message of response.messages ?? []) {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
      const list = answers.get(message.questionDefinitionIndex) ?? [];
      list.push(message);
      answers.set(message.questionDefinitionIndex, list);
    }
  }
  return answers;
}

function mergeAnswerMaps(maps: Map<string, MessageRef[]>[]): Map<string, MessageRef[]> {
  const merged = new Map<string, MessageRef[]>();
  for (const map of maps) {
    for (const [questionIndex, messages] of map) {
      const list = merged.get(questionIndex) ?? [];
      list.push(...messages);
      merged.set(questionIndex, list);
    }
  }
  return merged;
}

type GroupConfig = Pick<AnswerGroup, 'id' | 'label' | 'type'> &
  Partial<Pick<AnswerGroup, 'teamId' | 'respondentId' | 'isDefault' | 'initials' | 'teamNames'>> & {
    directRespondentIds?: Iterable<string>;
    directResponseIds?: Iterable<string>;
    children?: AnswerGroup[];
  };

/** Every changed cohort goes through this factory; display projections only reuse its results. */
export function createAnswerGroup(context: AnswerGroupContext, config: GroupConfig): AnswerGroup {
  const {
    directRespondentIds: memberIds = [],
    directResponseIds: channelIds = [],
    children = [],
    ...identity
  } = config;
  const directRespondentIds = new Set(memberIds);
  const directResponseIds = new Set(channelIds);
  const directResponses = [...directResponseIds].flatMap((id) => {
    const response = context.responsesById.get(id);
    return response ? [response] : [];
  });
  directResponses.forEach((response) => directRespondentIds.add(String(response.respondentId)));
  const directAnswers = collectAnswers(directResponses);
  const directQuestionSummaries = summarizeAnswers(directAnswers, context);
  const responseIds = new Set(directResponseIds);
  const respondentIds = new Set(directRespondentIds);
  let overlap = false;
  for (const child of children) {
    child.respondentIds.forEach((id) => respondentIds.add(id));
    for (const id of child.responseIds) {
      if (responseIds.has(id)) overlap = true;
      responseIds.add(id);
    }
  }
  const responses = [...responseIds].flatMap((id) => {
    const response = context.responsesById.get(id);
    return response ? [response] : [];
  });
  const answers =
    children.length === 0
      ? directAnswers
      : overlap
        ? collectAnswers(responses)
        : mergeAnswerMaps([directAnswers, ...children.map((child) => child.answers)]);
  const questionSummaries =
    children.length === 0
      ? directQuestionSummaries
      : overlap
        ? summarizeAnswers(answers, context)
        : mergeQuestionSummaries(
            [directQuestionSummaries, ...children.map((child) => child.questionSummaries)],
            context,
          );
  const respondingResponses = responses.filter(hasAnyMessage);
  const respondingRespondentIds = new Set(respondingResponses.map((response) => String(response.respondentId)));
  const teamNames = identity.teamNames ?? [
    ...new Set(
      [...respondentIds].map((id) => {
        const teamId = context.teamByRespondent.get(id);
        const team = teamId ? context.teamsById.get(teamId) : undefined;
        return team ? team.teamName || context.labels.withoutSupervisor : context.labels.unassigned;
      }),
    ),
  ];
  // Report-wide thresholds come from every response of the report, counts from the cohort.
  const directCustomMetrics = calculateCustomMetrics(
    context.customMetrics,
    directQuestionSummaries,
    directResponses,
    context.data.responses,
  );
  const customMetrics =
    questionSummaries === directQuestionSummaries
      ? directCustomMetrics
      : calculateCustomMetrics(context.customMetrics, questionSummaries, responses, context.data.responses);
  const group: AnswerGroup = {
    ...identity,
    children,
    teamNames,
    directRespondentIds,
    respondentIds,
    directResponseIds,
    responseIds,
    respondingRespondentIds,
    respondentCount: respondentIds.size,
    answerCount: respondingResponses.length,
    directAnswerCount: directResponses.filter(hasAnyMessage).length,
    answers,
    directAnswers,
    questionSummaries,
    directQuestionSummaries,
    customMetrics,
    directCustomMetrics,
    metrics: {
      responseRate: responses.length ? (100 * respondingResponses.length) / responses.length : undefined,
      anonymityRate: respondingResponses.length
        ? (100 * respondingResponses.filter((response) => response.originalAnonymous).length) /
          respondingResponses.length
        : undefined,
      additionallyAnonymized: respondingResponses.some((response) => response.additionallyAnonymized) ? 1 : 0,
    },
  };
  return group;
}

export function buildAnswerGroupTree(context: AnswerGroupContext): AnswerGroup[] {
  const memo = new Map<string, AnswerGroup>();
  const visiting = new Set<string>();
  const buildTeamGroup = (teamId: string): AnswerGroup | undefined => {
    if (memo.has(teamId)) return memo.get(teamId);
    const team = context.teamsById.get(teamId);
    if (!team || visiting.has(teamId)) return undefined;
    visiting.add(teamId);
    const children = (team.memberTeams ?? [])
      .map((id) => buildTeamGroup(String(id)))
      .filter((group): group is AnswerGroup => Boolean(group));
    const directRespondentIds = (team.directMembers ?? [])
      .map(String)
      .filter((id) => context.respondentsById.has(id) || context.responsesByRespondent.has(id));
    const directResponseIds = directRespondentIds.flatMap((id) =>
      (context.responsesByRespondent.get(id) ?? []).map((response) => response.id),
    );
    const group = createAnswerGroup(context, {
      id: `team:${teamId}`,
      label: team.teamName || context.labels.withoutSupervisor,
      type: 'team',
      teamId,
      teamNames: [team.teamName || context.labels.withoutSupervisor],
      children,
      directRespondentIds,
      directResponseIds,
    });
    visiting.delete(teamId);
    memo.set(teamId, group);
    return group;
  };
  const roots: AnswerGroup[] = context.data.reportRoots
    .map((id) => buildTeamGroup(String(id)))
    .filter((group): group is AnswerGroup => Boolean(group?.respondentCount))
    .map((group) => ({ ...group, isDefault: true }));

  const unassignedRespondents = new Set(
    [...context.respondentsById.keys(), ...context.responsesByRespondent.keys()].filter(
      (id) => !context.teamByRespondent.has(id),
    ),
  );
  if (unassignedRespondents.size > 0) {
    roots.push(
      createAnswerGroup(context, {
        id: 'custom:unassigned',
        label: context.labels.unassigned,
        type: 'custom',
        isDefault: true,
        teamNames: [context.labels.unassigned],
        directRespondentIds: unassignedRespondents,
        directResponseIds: [...unassignedRespondents].flatMap((id) =>
          (context.responsesByRespondent.get(id) ?? []).map((response) => response.id),
        ),
      }),
    );
  }
  return roots;
}
